import React, { useState } from 'react';
import { useSeller } from '../../context/SellerContext';
import InputField from '../InputField';
import FAQSection from '../FAQSection';
import {
  validatePAN,
  validateGSTIN,
  validateFullName,
  validateEmail,
} from '../../utils/validation';
import { sellerAuthAPI, handleAPIError } from '../../utils/sellerAPI';

const TURNOVER_OPTIONS = [
  { value: 'below-20l', label: 'Below ₹20 Lakhs' },
  { value: '20l-40l', label: '₹20 - 40 Lakhs' },
  { value: 'above-40l', label: 'Above ₹40 Lakhs (GST Required)' },
];

const BusinessDetailsForm = ({ onNext, onPrev }) => {
  const {
    formData,
    updateFormData,
    errors,
    setError,
    clearAllErrors,
    loading,
    setLoading,
  } = useSeller();

  const [showEIDSection, setShowEIDSection] = useState(false);
  const [verifyingEnrolment, setVerifyingEnrolment] = useState(false);
  const [verifyingGST, setVerifyingGST] = useState(false);
  const [gstVerified, setGstVerified] = useState(false);
  const [gstBusinessName, setGstBusinessName] = useState('');

  const handleGSTChange = (value) => {
    updateFormData('hasGST', value === 'yes');
    setGstVerified(false);
    setGstBusinessName('');
    clearAllErrors();
  };

  const handleGSTINChange = (e) => {
    updateFormData('gstNumber', e.target.value.toUpperCase());
    setGstVerified(false);
    clearAllErrors('gstNumber');
  };

  const handlePANChange = (e) => {
    updateFormData('panNumber', e.target.value.toUpperCase());
    clearAllErrors('panNumber');
  };

  const handleTurnoverChange = (e) => {
    updateFormData('expectedTurnover', e.target.value);
    clearAllErrors('expectedTurnover');
  };

  const handleVerifyGSTIN = async () => {
    const gstError = validateGSTIN(formData.gstNumber);
    if (gstError) {
      setError('gstNumber', gstError);
      return;
    }

    setVerifyingGST(true);
    try {
      const response = await sellerAuthAPI.verifyGSTIN(formData.gstNumber, formData.expectedTurnover);
      if (response.data.success && response.data.verified) {
        setGstVerified(true);
        setGstBusinessName(response.data.businessName || '');
        clearAllErrors('gstNumber');
      }
    } catch (err) {
      const apiError = handleAPIError(err);
      setError('gstNumber', apiError.message);
    } finally {
      setVerifyingGST(false);
    }
  };

  const handleEnrolmentVerify = async (e) => {
    e.preventDefault();

    if (!formData.enrolmentId) {
      setError('enrolmentId', 'Please enter Enrolment ID');
      return;
    }

    setVerifyingEnrolment(true);
    try {
      const response = await sellerAuthAPI.verifyEnrolment(formData.enrolmentId);
      if (response.status === 200) {
        updateFormData('enrolmentDetails', response.data);
        clearAllErrors();
      }
    } catch (err) {
      const apiError = handleAPIError(err);
      setError('enrolmentId', apiError.message);
    } finally {
      setVerifyingEnrolment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const validationErrors = {};

    if (formData.hasGST === null) {
      validationErrors.hasGST = 'Please select GST status';
    } else if (formData.hasGST === true) {
      const gstError = validateGSTIN(formData.gstNumber);
      if (gstError) validationErrors.gstNumber = gstError;
    } else if (!formData.enrolmentDetails && formData.hasGST === false) {
      // Non-GST seller creating new enrollment
      const panError = validatePAN(formData.panNumber);
      const nameError = validateFullName(formData.fullName);
      const emailError = validateEmail(formData.email);

      if (panError) validationErrors.panNumber = panError;
      if (nameError) validationErrors.fullName = nameError;
      if (emailError) validationErrors.email = emailError;
    }

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, error]) => {
        setError(field, error);
      });
      return;
    }

    setLoading(true);
    try {
      if (formData.hasGST === false && !formData.enrolmentDetails) {
        // Create new enrolment
        const response = await sellerAuthAPI.createEnrolment({
          panNumber: formData.panNumber,
          fullName: formData.fullName,
          email: formData.email,
        });

        if (response.status === 200) {
          updateFormData('enrolmentDetails', response.data);
          updateFormData('enrolmentId', response.data.enrolmentId);
        }
      }

      onNext();
    } catch (err) {
      const apiError = handleAPIError(err);
      setError('submit', apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const businessFAQs = [
    {
      question: 'Which sellers can sell on Kalinga?',
      answer:
        'Starting October 1st, 2023, sellers (with or without GST registration) can sell on Kalinga.',
    },
    {
      question: 'How can I obtain GSTIN No or Enrolment ID / UIN?',
      answer:
        'You can obtain these from the GST website or through government portals. Watch video guides for step-by-step instructions.',
    },
    {
      question: 'What is the difference between Enrolment ID / UIN and GSTIN?',
      answer:
        'Enrolment ID is for non-GST sellers, while GSTIN is for registered GST businesses.',
    },
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
      <div className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* GST Status Selection */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Do you have a GST number?
            </h3>

            <div className="flex flex-col gap-4 lg:flex-row">
              <label
                className={`flex-1 flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition ${formData.hasGST === true
                  ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <input
                  type="radio"
                  name="hasGST"
                  value="yes"
                  checked={formData.hasGST === true}
                  onChange={() => handleGSTChange('yes')}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <span>
                  <span className="font-semibold text-gray-900 block">Yes</span>
                  <span className="text-sm text-gray-600">
                    Enter your GSTIN and sell anywhere easily
                  </span>
                </span>
              </label>

              <label
                className={`flex-1 flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition ${formData.hasGST === false
                  ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <input
                  type="radio"
                  name="hasGST"
                  value="no"
                  checked={formData.hasGST === false}
                  onChange={() => handleGSTChange('no')}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <span>
                  <span className="font-semibold text-gray-900 block">No</span>
                  <span className="text-sm text-gray-600">
                    Worry not, you can sell without GST{' '}
                    <span className="text-green-600 font-semibold">
                      Get EID in mins
                    </span>
                  </span>
                </span>
              </label>
            </div>

            {errors.hasGST && (
              <p className="text-red-500 text-sm mt-4">{errors.hasGST}</p>
            )}
          </div>

          {/* GST Number Input with Verification */}
          {formData.hasGST === true && (
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GSTIN Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="gstNumber"
                    value={formData.gstNumber || ''}
                    onChange={handleGSTINChange}
                    placeholder="22ABCDE1234F1Z0"
                    maxLength="15"
                    className={`
                      flex-1 px-4 py-2 border border-gray-300 rounded-lg uppercase
                      focus:outline-none focus:ring-2 focus:ring-blue-500
                      ${errors.gstNumber ? 'border-red-500 bg-red-50' : ''}
                      ${gstVerified ? 'border-green-500 bg-green-50' : ''}
                    `}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyGSTIN}
                    disabled={verifyingGST || !formData.gstNumber || gstVerified}
                    className={`
                      px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap
                      ${gstVerified
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : 'bg-blue-600 text-white hover:bg-blue-700'}
                      ${verifyingGST || !formData.gstNumber ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {verifyingGST ? 'Verifying...' : gstVerified ? '✓ Verified' : 'Verify'}
                  </button>
                </div>
                {errors.gstNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.gstNumber}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  Format: 22ABCDE1234F1Z0
                </p>
              </div>

              {/* Show verified business name */}
              {gstVerified && gstBusinessName && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <span className="font-semibold">Business Name:</span> {gstBusinessName}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Expected Turnover - shown for non-GST sellers */}
          {formData.hasGST === false && (
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Annual Turnover
              </label>
              <select
                value={formData.expectedTurnover || ''}
                onChange={handleTurnoverChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select expected turnover</option>
                {TURNOVER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {formData.expectedTurnover === 'above-40l' && (
                <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Businesses with turnover above ₹40 Lakhs require GST registration.
                    Your account will be flagged for compliance review.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Non-GST Section */}
          {formData.hasGST === false && (
            <div className="space-y-4">
              {/* Create New Enrollment */}
              {!formData.enrolmentDetails && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Sell without GST in minutes
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    We only need the below details from you to create your
                    enrolment ID
                  </p>

                  <div className="space-y-4">
                    <InputField
                      label="PAN Number"
                      type="text"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={handlePANChange}
                      placeholder="Enter PAN (e.g., ABCDE1234F)"
                      error={errors.panNumber}
                      maxLength="10"
                      required
                    />

                    <InputField
                      label="Full Name"
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={(e) => {
                        updateFormData('fullName', e.target.value);
                        clearAllErrors('fullName');
                      }}
                      placeholder="Full name as per PAN"
                      error={errors.fullName}
                      required
                    />

                    <InputField
                      label="Email ID"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => {
                        updateFormData('email', e.target.value);
                        clearAllErrors('email');
                      }}
                      placeholder="Enter email address"
                      error={errors.email}
                      required
                    />

                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className={`w-full py-3 rounded-lg font-semibold text-white transition ${loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                    >
                      {loading ? 'Creating Enrolment...' : 'Proceed to add details'}
                    </button>

                    <div className="text-center pt-4 border-t border-indigo-100">
                      <p className="text-sm text-gray-600">
                        Or create it directly through the{' '}
                        <a
                          href="https://gst.gov.in"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 font-semibold hover:underline"
                        >
                          GST website
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Existing Enrollment */}
              <button
                type="button"
                onClick={() => setShowEIDSection(!showEIDSection)}
                className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition flex items-center justify-between shadow-sm"
              >
                <span className="font-semibold text-gray-900">
                  Add existing enrolment ID
                </span>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${showEIDSection ? 'rotate-180' : ''
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </button>

              {showEIDSection && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4 shadow-sm">
                  <InputField
                    label="Enrolment ID / UIN"
                    type="text"
                    name="enrolmentId"
                    value={formData.enrolmentId}
                    onChange={(e) => {
                      updateFormData('enrolmentId', e.target.value.toUpperCase());
                      clearAllErrors('enrolmentId');
                    }}
                    placeholder="Enter Enrolment ID"
                    error={errors.enrolmentId}
                  />

                  <button
                    type="button"
                    onClick={handleEnrolmentVerify}
                    disabled={verifyingEnrolment || !formData.enrolmentId}
                    className={`w-full py-3 rounded-lg font-semibold text-white transition ${verifyingEnrolment || !formData.enrolmentId
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                  >
                    {verifyingEnrolment ? 'Verifying...' : 'Verify'}
                  </button>

                  {formData.enrolmentDetails && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-700 font-semibold mb-3">
                        ✓ Enrolment Verified
                      </p>
                      <div className="space-y-2 text-sm">
                        <div>
                          <p className="text-gray-500">Name</p>
                          <p className="font-semibold text-gray-900">
                            {formData.enrolmentDetails.name}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">PAN Number</p>
                          <p className="font-semibold text-gray-900">
                            {formData.enrolmentDetails.panNumber}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Business Address</p>
                          <p className="font-semibold text-gray-900">
                            {formData.enrolmentDetails.address}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-4 sm:flex-row">
            <button
              type="button"
              onClick={onPrev}
              className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-3 rounded-lg font-semibold text-white transition ${loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
            >
              {loading ? 'Processing...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <p className="text-sm font-semibold text-gray-800">
            More than 100,000 suppliers are growing their business by selling on
            Kalinga
          </p>
        </div>
        <FAQSection faqs={businessFAQs} />
      </div>
    </div>
  );
};

export default BusinessDetailsForm;
